import React, { useEffect, useMemo, useRef, useState } from 'react';
import {NumericFormat} from "react-number-format";
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { claimActions, httpRequestsOnSuccessActions } from 'store';
import { FindLoad, FindSuccess, Images, useModal } from 'utils';
import { Input, PriceRow, SelectAutocompleteMultiple, TextareaCustom, CreateChancel, TextRow } from 'components';
import { casGroupOptions, CAS_GROUP, getCarcSuggestion } from './constnats';

const ACTION_TYPE = 'UPDATE_BILL_ADJUSTMENTS';
const EMPTY_FORM = { group: '', reasonCode: '', amount: '', reasonText: '' };

const toNumber = (val) => {
    const parsed = parseFloat(String(val ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const buildInitialAdjustments = (item) =>
    (Array.isArray(item?.adjustments) ? item.adjustments : []).map((adj, i) => ({
        id: adj?._id || adj?.id || `seed-${i}`,
        group: adj?.claimAdjustmentGroupCode || CAS_GROUP.OA,
        reasonCode: adj?.adjustmentReasonCode ?? '',
        amount: toNumber(adj?.adjustmentAmount),
        reasonText: adj?.adjustmentReason || '',
    }));

export const ClaimAdjustment = ({ item, claim, afterSuccess }) => {
    const dispatch = useDispatch();
    const { control, handleSubmit, reset, watch, setValue } = useForm({ defaultValues: EMPTY_FORM });
    const { close } = useModal();
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccess(ACTION_TYPE);
    const [adjustments, setAdjustments] = useState(() => buildInitialAdjustments(item));
    const [activeForm, setActiveForm] = useState(null);
    const idCounter = useRef(0);
    const claimId = claim?.id;
    const billId = item?.id || item?._id || item?.billId;
    const claimLabel = claim?.displayId || '--';
    const dos = item?.dos ? moment.utc(item.dos).format('MM/DD/YYYY') : '--';
    const units = item?.totalUnits ?? '--';

    const totalAdjustment = useMemo(() => adjustments.reduce((s, a) => s + toNumber(a.amount), 0), [adjustments]);
    const totalCount = adjustments.length;

    useEffect(() => {
        if (success?.length) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            if (afterSuccess) afterSuccess();
            close();
        }
    }, [success]);

    const startAdd = () => {
        setActiveForm({ mode: 'add' });
        reset(EMPTY_FORM);
    };

    const startEdit = (adj) => {
        setActiveForm({ mode: 'edit', adjId: adj.id });
        reset({
            group: adj.group,
            reasonCode: adj.reasonCode ?? '',
            amount: adj.amount === 0 || adj.amount ? String(adj.amount) : '',
            reasonText: adj.reasonText || '',
        });
    };

    const cancelForm = () => {
        setActiveForm(null);
        reset(EMPTY_FORM);
    };

    const handleReasonCodeChange = (e) => {
        const value = e?.target?.value;
        if (value && !watch('reasonText')) {
            const suggestion = getCarcSuggestion(value);
            if (suggestion) setValue('reasonText', suggestion);
        }
    };

    const deleteAdjustment = (adjId) => {
        setAdjustments((prev) => prev.filter((a) => a.id !== adjId));
        if (activeForm?.adjId === adjId) cancelForm();
    };

    const submitForm = (data) => {
        const payload = {
            group: data.group,
            reasonCode: String(data.reasonCode || '').trim(),
            amount: toNumber(data.amount),
            reasonText: String(data.reasonText || '').trim(),
        };

        setAdjustments((prev) => {
            if (activeForm?.mode === 'edit') {
                return prev.map((a) => (a.id === activeForm.adjId ? { ...a, ...payload } : a));
            }
            idCounter.current += 1;
            return [...prev, { id: `new-${idCounter.current}`, ...payload }];
        });
        cancelForm();
    };

    const handleSave = () => {
        if (!claimId || !billId || activeForm || loader?.length) return;
        const body = adjustments.map((a) => ({
            claimAdjustmentGroupCode: a.group,
            adjustmentReasonCode: String(a.reasonCode || '').trim(),
            adjustmentAmount: String(toNumber(a.amount)),
            adjustmentReason: String(a.reasonText || '').trim(),
        }));
        dispatch(claimActions.updateBillAdjustments(claimId, billId, body));
    };

    const renderForm = (submitLabel) => (
        <div className="adj-form">
            <div className="adj-form-row">
                <SelectAutocompleteMultiple
                    name="group"
                    label="CAS Group*"
                    control={control}
                    options={casGroupOptions}
                    renderValue={(option) => option?.name}
                    rules={{ required: 'CAS group is required.' }}
                />
                <Input
                    name="reasonCode"
                    label="Reason Code"
                    variant="outlined"
                    control={control}
                    onChange={handleReasonCodeChange}
                    placeholder="e.g. 45"
                    maxLength={5}
                />
                <Input name="amount" label="Amount ($)" variant="outlined" control={control} placeholder="0.00" />
            </div>
            <TextareaCustom name="reasonText" label="Reason Text" control={control} maxRows={4} maxLength={250} />

            <div className="adj-form-actions">
                <CreateChancel
                    create={submitLabel}
                    chancel={'Cancel'}
                    onCreate={handleSubmit(submitForm)}
                    onClose={cancelForm}
                    buttonWidth="150px"
                    cancelButtonWidth='75px'
                    height='36px'
                    justifyContent='flex-end'
                />
            </div>
        </div>
    );

    const isAdding = activeForm?.mode === 'add';

    return (
        <div className="claim-adjustment-modal">
            <div className="adj-header">
                <p className="adj-header-label">Adjustment Details</p>
                <p className="adj-header-title">CPT {item?.cptCode || '--'}</p>
                <p className="adj-header-label" style={{ marginBottom: 0 }}>
                    Claim {claimLabel} · Bill {item?.displayId || '--'} · DOS {dos} · {units} units
                </p>
            </div>

            <div className="adj-summary">
                <div className="adj-summary-cell">
                    <span className="adj-summary-label">Billed</span>
                    <span className="adj-summary-value">
                        <PriceRow info={item?.totalBilled ? item.totalBilled : 0} />
                    </span>
                </div>
                <div className="adj-summary-cell">
                    <span className="adj-summary-label">Allowed</span>
                    <span className="adj-summary-value">
                        <PriceRow info={item?.allowedAMT ? item.allowedAMT : 0} />
                    </span>
                </div>
                <div className="adj-summary-cell">
                    <span className="adj-summary-label">Paid</span>
                    <span className="adj-summary-value">
                        <PriceRow info={item?.payorPaid ? item.payorPaid : 0} />
                    </span>
                </div>
                <div className="adj-summary-cell total">
                    <span className="adj-summary-label">Total adjustment</span>
                    <span className="adj-summary-value">
                        <PriceRow info={totalAdjustment} />
                    </span>
                </div>
            </div>

            {/* Adjustments */}
            <div className="adj-remittance">
                <div className="adj-remittance-head">
                    <div className="adj-remittance-head-left">
                        <img src={Images.dollarIcon} alt="dollarIcon" />
                        <div>
                            <span className="adj-remittance-id">Adjustments (CAS)</span>
                            <p className="adj-remittance-meta">
                                {[item?.payerName || item?.funderName, item?.chargeRateName].filter(Boolean).join(' · ') ||
                                    'Claim adjustment segments for this bill'}
                            </p>
                        </div>
                    </div>
                    <div className="adj-remittance-subtotal">
                        <span className="label">Subtotal:</span>
                        <span className="value">
                            <NumericFormat value={totalAdjustment} displayType={'text'} thousandSeparator={true} prefix={'$'} />
                        </span>
                    </div>
                </div>

                <div className="adj-table-head">
                    <span className="col-code">Code</span>
                    <span className="col-reason">Reason</span>
                    <span className="col-amount">Amount</span>
                    <span className="col-action">Action</span>
                </div>

                {!adjustments.length && !isAdding && <p className="adj-empty">No adjustments yet.</p>}

                {adjustments.map((adj) => {
                    const editingThis = activeForm?.mode === 'edit' && activeForm?.adjId === adj.id;
                    if (editingThis) {
                        return <React.Fragment key={adj.id}>{renderForm('Save')}</React.Fragment>;
                    }
                    return (
                        <div className="adj-row" key={adj.id}>
                            <span className={`cas-pill cas-${adj.group}`}>
                                {adj.group}
                                {adj.reasonCode ? `-${adj.reasonCode}` : ''}
                            </span>
                            <span className="adj-reason">{adj.reasonText || '--'}</span>
                            <span className="adj-amount">
                                <PriceRow info={toNumber(adj.amount)} />
                            </span>
                            <span className="adj-actions">
                                <button type="button" onClick={() => startEdit(adj)} aria-label="Edit adjustment">
                                    <img src={Images.editDarkBlue} alt="edit" />
                                </button>
                                <button type="button" onClick={() => deleteAdjustment(adj.id)} aria-label="Delete adjustment">
                                    <img src={Images.redTrash} alt="delete" />
                                </button>
                            </span>
                        </div>
                    );
                })}

                {isAdding && renderForm('Add Adjustment')}

                {!activeForm && (
                    <div className="adj-add-row">
                        <button type="button" className="adj-add-link" onClick={startAdd}>
                            + Add Adjustment
                        </button>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="adj-footer">
                <span className="adj-footer-count">
                    {totalCount} adjustment{totalCount !== 1 ? 's' : ''} on this bill
                </span>

                <CreateChancel
                    // loader={!!loader?.length}
                    create={'Save Adjustments'}
                    chancel={'Cancel'}
                    onCreate={handleSave}
                    onClose={close}
                    buttonWidth="170px"
                    cancelButtonWidth="75px"
                    height="36px"
                    justifyContent="flex-end"
                    disabled={!claimId || !billId || !!activeForm || !!loader?.length}
                    loader={!!loader?.length}
                />

                {/*<div className="adj-footer-actions">*/}
                {/*    <button type="button" className="adj-footer-cancel" onClick={close}>*/}
                {/*        Cancel*/}
                {/*    </button>*/}
                {/*    <button*/}
                {/*        type="button"*/}
                {/*        className="adj-footer-save"*/}
                {/*        onClick={handleSave}*/}
                {/*        disabled={!claimId || !billId || !!activeForm || !!loader?.length}>*/}
                {/*        {loader?.length ? <MinLoader margin={'0'} color={Colors.TextWhite} position={'relative'} /> : 'Save Adjustments'}*/}
                {/*    </button>*/}
                {/*</div>*/}
            </div>
        </div>
    );
};

export const ClaimAdjustmentCell = ({ item, claim, afterSuccess }) => {
    const { open } = useModal();
    const summary = item?.adjustments?.map((a) => `${a?.claimAdjustmentGroupCode}-${a?.adjustmentReasonCode}`).join(', ') || '--';

    const handleOpen = () => open(<ClaimAdjustment claim={claim} item={item} afterSuccess={afterSuccess} />);

    return (
        <div className="adj-cell-trigger" onClick={handleOpen} style={{ textDecoration: 'underline', color: '#347AF0', cursor: 'pointer' }}>
            <TextRow name={summary} />
        </div>
    );
};
